import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { resolveHostRedirect } from '../config/domains';

// ---------------------------------------------------------------------------
// DomainRouter — enforces the marketing / platform hostname split on every
// navigation (§13.0 step 25).
//
// Wraps the whole route table. On each location change it asks config/domains
// whether this hostname should be serving this path, and if not it leaves:
//
//   recurr.co.za/login       → https://app.recurr.co.za/login   (full load)
//   app.recurr.co.za/        → /login                           (in-app)
//   anything on localhost, dev, a merchant domain → rendered as-is
//
// It sits ABOVE <Suspense> on purpose. The redirect decision needs no lazy
// chunk, so a visitor who lands on the wrong host is moved immediately instead
// of waiting for a page they are never going to see to finish downloading.
//
// The children are not rendered while a cross-host redirect is in flight —
// otherwise the marketing host would paint the login form for the moment
// before the browser navigates, which reads as a flash of the wrong site.
// ---------------------------------------------------------------------------

const DomainRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const redirect = resolveHostRedirect(pathname, search, hash);

  // Depend on the URL string, not the object — resolveHostRedirect builds a
  // fresh object every render, and an object dependency would re-fire the
  // navigation on every one of them.
  const externalUrl = redirect?.kind === 'external' ? redirect.url : null;

  useEffect(() => {
    // replace(), not assign(): the wrong-host URL should not become a back-
    // button trap that bounces the visitor straight back out again.
    if (externalUrl) window.location.replace(externalUrl);
  }, [externalUrl]);

  if (redirect?.kind === 'internal') {
    return <Navigate to={redirect.to} replace />;
  }

  if (externalUrl) {
    // Shown for the instant before the browser leaves. The anchor is the
    // fallback for the case where a browser or extension blocks the scripted
    // navigation — without it that visitor sees a sentence and no way on.
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: 12,
        fontSize: '1.05rem', color: '#4b5563', textAlign: 'center', padding: 24,
      }}>
        <span>Taking you to the ReCurr app…</span>
        <a href={externalUrl} style={{ color: '#1e2c4f', fontWeight: 600 }}>
          Continue →
        </a>
      </div>
    );
  }

  return <>{children}</>;
};

export default DomainRouter;
